/**
 * Fill recipient/company merge fields in admin-facing SafeAttack previews with a
 * sample identity, so a preview reads like a real personalised message instead
 * of leaking raw template code. Covers the Go-template syntax the king-phisher
 * library uses (`{{ .Recipient.FirstName }}` — #1000) and the bracketed
 * placeholders the AI/real-attack content uses (`[Recipient Name]`,
 * `[Recipient Company Name]` — #1044).
 *
 * IMPORTANT: substitution is an explicit ALLOWLIST of known recipient/company
 * fields. A generic `[...]` sweep is deliberately avoided — the library is full
 * of legitimate bracketed pretext (`[CRITICAL ACTION REQUIRED]`,
 * `[Contract #WIPRO-PX239-07]`, `[Candidate Name 1]`, `[Date, Time, Location]`)
 * that is real email content and must render verbatim.
 *
 * This is for PREVIEW only. The play portal personalises with the learner's own
 * details at render time; this sample resolver is the admin-side stand-in.
 */

const SAMPLE = {
  firstName: 'Alex',
  lastName: 'Morgan',
  name: 'Alex Morgan',
  email: 'alex.morgan@example.com',
  company: 'Acme Corporation',
  position: 'Account Manager',
  department: 'Finance',
}

// Neutral stand-in for `{{companyLogo}}` in ADMIN previews. The play portal
// substitutes the tenant's real logo at render time (safe-attack-personalizer:
// `{{companyLogo}}` -> tenant logo URL, fallback the HumanFirewall wordmark);
// here we show a self-contained neutral mark so the preview never renders a
// broken image or leaks the raw token. Kept as a data-URI so the downstream
// resolveBrandImages() pass leaves it untouched (a data: src is not "broken").
const SAMPLE_COMPANY_LOGO =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 140 40" width="140" height="40">' +
      '<rect width="140" height="40" rx="6" fill="#eceef1"/>' +
      '<rect x="14" y="12" width="16" height="16" rx="2" fill="#b6bcc6"/>' +
      '<rect x="18" y="16" width="3" height="3" fill="#eceef1"/>' +
      '<rect x="23" y="16" width="3" height="3" fill="#eceef1"/>' +
      '<rect x="18" y="21" width="3" height="3" fill="#eceef1"/>' +
      '<rect x="23" y="21" width="3" height="3" fill="#eceef1"/>' +
      '<rect x="40" y="15" width="60" height="5" rx="2.5" fill="#c4c9d2"/>' +
      '<rect x="40" y="24" width="40" height="4" rx="2" fill="#d6dae0"/>' +
      '</svg>',
  )

function sampleYear(): string {
  return String(new Date().getFullYear())
}

function samplePlusDays(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// Ordered [pattern, value]. More-specific fields (first/last/company) precede
// the generic name so `[Recipient Company Name]` is never mis-read as a name.
const RULES: Array<[RegExp, () => string]> = [
  // ── Go-template (king-phisher origin) ──────────────────────────────────────
  [/\{\{\s*\.recipient\.first_?name\s*\}\}/gi, () => SAMPLE.firstName],
  [/\{\{\s*\.recipient\.last_?name\s*\}\}/gi, () => SAMPLE.lastName],
  [/\{\{\s*\.recipient\.(full_?name|name)\s*\}\}/gi, () => SAMPLE.name],
  [/\{\{\s*\.recipient\.email\s*\}\}/gi, () => SAMPLE.email],
  [
    /\{\{\s*\.recipient\.(position|title|job_?title)\s*\}\}/gi,
    () => SAMPLE.position,
  ],
  [/\{\{\s*\.recipient\.department\s*\}\}/gi, () => SAMPLE.department],
  [/\{\{\s*\.(company|organization)(\.name)?\s*\}\}/gi, () => SAMPLE.company],
  // ── Handlebars / snake_case ────────────────────────────────────────────────
  [/\{\{\s*recipient_first_name\s*\}\}/gi, () => SAMPLE.firstName],
  [/\{\{\s*recipient_last_name\s*\}\}/gi, () => SAMPLE.lastName],
  [/\{\{\s*recipient_name\s*\}\}/gi, () => SAMPLE.name],
  [/\{\{\s*(recipient_email|email)\s*\}\}/gi, () => SAMPLE.email],
  // Logo token — must precede the bare {{company}} rule so it is never partially
  // matched. Resolves to a neutral sample logo (usually inside an <img src>).
  [/\{\{\s*companyLogo\s*\}\}/gi, () => SAMPLE_COMPANY_LOGO],
  [/\{\{\s*(company_name|company|organization)\s*\}\}/gi, () => SAMPLE.company],
  [/\{\{\s*current_year\s*\}\}/gi, sampleYear],
  [/\{\{\s*date_plus_2_days\s*\}\}/gi, () => samplePlusDays(2)],
  // ── Bracketed — ALLOWLISTED recipient/company phrases only ──────────────────
  [/\[recipient'?s?\s+first\s+name\]/gi, () => SAMPLE.firstName],
  [/\[recipient'?s?\s+last\s+name\]/gi, () => SAMPLE.lastName],
  [/\[recipient'?s?\s+company(\s+name)?\]/gi, () => SAMPLE.company],
  [/\[recipient'?s?\s+email\]/gi, () => SAMPLE.email],
  [/\[recipient_?email\]/gi, () => SAMPLE.email],
  [/\[recipient'?s?\s+name\]/gi, () => SAMPLE.name],
]

export function resolveSamplePlaceholders(
  text: string | null | undefined,
): string {
  if (!text) return ''
  let out = text
  for (const [re, val] of RULES) out = out.replace(re, val())
  return out
}
